/**
 * Service editor — full create/edit, single page with a left section-nav.
 * Verbatim port of screens-service-editor.jsx.
 *
 * Sections: Overview · Delivery (tabs per method) · Booking & approval ·
 * Staff & resources · Coverage · Targeting & extras. Persists the full rich
 * shape to ServicesStore. (Per F-102 the hero image lives on Overview and
 * there is no Landing-page tab.)
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { label as tenantLabel } from "../../lib/labels";
import { Chip, IconBtn, Btn, Checkbox, Icon, Input, Pill, T, Toggle } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { EmptyState } from "../../shell/composites";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { useStaff } from "../staff/useStaff";
import { useResources } from "../resources/useResources";
import {
  SVC_CHANNELS, useServices, ensureServiceShape, svcCategoryName, _svcSlug, type Service,
  uploadServiceImage, withServiceImage, serviceImageOf, serviceImageAssetIdOf,
  serviceImageTypeError, serviceImageSizeError, serviceImageBusy, SERVICE_IMAGE_IDLE, type ServiceImageUploadState, ServicesStore } from "./services-data";
import { svcStatusPill, SvcChannelGlyph } from "./ServicesScreens";
import { useProducts, productPriceLabel } from "../products/useProducts";
import { ServiceDeliverySection, ExceptionList } from "./ServiceDelivery";
import { ResolvesHoldsPicker, holdsResolvesLabel } from "../../lib/holds";
import { AudienceSection, audienceErrorsFrom, audienceStoreUnavailable, type AudienceErrors } from "../../lib/audience";
import {
  SvcRow, SvcSeg, SvcSelect, SvcNumField, SvcTagField, SvcSectionHead, SvcLocationPicker,
  Radio, ServiceImageField, prepareServiceImage, serviceReadiness, serviceCoverageMatrix,
  type ServiceImageHandlers,
} from "./service-editor-controls";
import { useT } from "../../lib/i18n";

/* ═══ Editor shell ════════════════════════════════════════════════════════ */
export default function ServiceEditorPage() {
  const t = useT();
  const nav = useNav();
  const store = useServices();
  const isNew = !!(nav.params && nav.params.new);
  const viewOnly = !!(nav.params && nav.params.view);
  const id = nav.params && (nav.params.id as string | undefined);
  const canWrite = !viewOnly && can("services.write");

  const [form, setForm] = useState<Service>(() => {
    if (isNew || !id) return store.newService();
    const s = store.getService(id);
    return s ? ensureServiceShape({ ...s }) : store.newService();
  });
  /* COLD LOAD (CC live test, 2026-09-25): opened by link or refresh, the seed ran
     before the catalogue had loaded and fell back to a blank draft, and Save then
     sent nothing. Re-seed once the service arrives, only while the form is still
     that placeholder; an unknown id is an error state, never a new service. */
  const hydratedSvc = !isNew && id ? store.getService(id) : null;
  useEffect(() => {
    if (hydratedSvc && form.id !== id) setForm(ensureServiceShape({ ...hydratedSvc }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedSvc ? hydratedSvc.id : null]);
  const svcNotFound = !isNew && !!id && ServicesStore.loaded() && !hydratedSvc;
  const svcPending = !isNew && !!id && !ServicesStore.loaded() && !hydratedSvc;
  /* `set` takes a patch OR a function of the LATEST form (CD docket 2026-09-15,
     service hero image): the name field rewrites `landing.slug` on every
     keystroke, so a patch built from a `form.landing` captured before an
     upload's `await` clobbered the slug with a stale copy. */
  const set = (patch: Partial<Service> | ((f: Service) => Partial<Service>)) =>
    setForm((f) => ({ ...f, ...(typeof patch === "function" ? patch(f) : patch) }));
  const setMethodCfg = (methodId: string, patch: Record<string, any>) => setForm((f) => ({ ...f, methodConfig: { ...f.methodConfig, [methodId]: { ...f.methodConfig[methodId], ...patch } } }));

  const [section, setSection] = useState("overview");
  /* JAI-2020: 422 AUDIENCE_INVALID per condition, and 503 AUDIENCE_STORE_UNAVAILABLE
     as a section-level error. Both clear on the next audience edit. */
  const [audienceErrors, setAudienceErrors] = useState<AudienceErrors | null>(null);
  const [audienceNotice, setAudienceNotice] = useState<string | null>(null);
  /* Service image — the typed service asset lifecycle (queued → preparing →
     uploading → confirming → saved | failed). Upload state lives on the shell,
     not the section, because `save` has to refuse while it is in flight — that
     window is the original symptom: saving mid-upload persisted the interim
     `blob:` URL. The form only ever receives a CONFIRMED asset: its id plus the
     URL the confirm response returned. A preview object URL never reaches it.

     Detach-only: removing, replacing, abandoning or superseding an image clears
     this service's REFERENCE to the asset and never deletes the asset, which
     may be shared with another record (see services-data.ts). That is why there
     is no archive bookkeeping here — nothing is owed at unmount or after save. */
  const [imageUpload, setImageUpload] = useState<ServiceImageUploadState>(SERVICE_IMAGE_IDLE);
  const formRef = useRef(form);
  formRef.current = form;
  const uploadRun = useRef(0);
  const uploadAbort = useRef<AbortController | null>(null);
  // Leaving without saving: stop the in-flight upload so a late confirm cannot
  // write into an unmounted form. Whatever the server already made stays put.
  useEffect(() => () => {
    uploadRun.current++;
    if (uploadAbort.current) uploadAbort.current.abort();
  }, []);

  const cancelImageUpload = () => {
    uploadRun.current++;
    if (uploadAbort.current) uploadAbort.current.abort();
    uploadAbort.current = null;
  };
  const startImageUpload = async (file: File) => {
    cancelImageUpload();
    const run = uploadRun.current;
    const live = () => run === uploadRun.current;
    const typeError = serviceImageTypeError(file);
    if (typeError) { setImageUpload({ stage: "failed", error: typeError, file }); return; }
    /* Size is judged on the ORIGINAL pick, before prepareServiceImage resizes or
       re-encodes anything and before any presign — so an over-limit image is
       refused inline having issued ZERO upload requests. Downscaling an
       oversized file into the limit would upload something never chosen. */
    const sizeError = serviceImageSizeError(file);
    if (sizeError) { setImageUpload({ stage: "failed", error: sizeError, file }); return; }
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    uploadAbort.current = controller;
    setImageUpload({ stage: "queued", error: "", file });
    try {
      // Let "queued" paint: set in the same tick as "preparing", React batches it away.
      await new Promise<void>((resolve) => (typeof requestAnimationFrame === "function" ? requestAnimationFrame(() => resolve()) : setTimeout(resolve, 0)));
      if (!live()) return;
      setImageUpload({ stage: "preparing", error: "", file });
      const prepared = await prepareServiceImage(file);
      if (!live()) return;
      const confirmed = await uploadServiceImage({
        ...prepared,
        originalSize: file.size,
        serviceId: formRef.current.id,
        signal: controller ? controller.signal : undefined,
        onStage: (stage, progress) => { if (live()) setImageUpload({ stage, progress, error: "", file }); },
      });
      // Superseded while confirming (a newer pick, Remove, or unmount): drop the
      // result. The confirmed asset is left alone — it may be a deduplicated
      // match another record already points at.
      if (!live()) return;
      set((f) => withServiceImage(f, confirmed.url, confirmed.assetId));
      setImageUpload({ stage: "saved", progress: 1, error: "", file });
    } catch (e) {
      if (!live()) return;
      /* A thrown message here is a SPECIFIC, user-actionable reason (over the
         5 MB limit, not a supported image, rejected upload) — show it instead
         of the generic line. */
      const msg = String((e as { message?: string })?.message || "").trim();
      setImageUpload({ stage: "failed", error: msg || t("services.editorPage.imageUploadFailed", "That image couldn’t be uploaded, so the service image is unchanged."), file });
    }
  };
  const image: ServiceImageHandlers = {
    upload: imageUpload,
    pick: (file) => { void startImageUpload(file); },
    retry: () => { if (imageUpload.file) void startImageUpload(imageUpload.file); },
    dismiss: () => { cancelImageUpload(); setImageUpload(SERVICE_IMAGE_IDLE); },
    /* Remove DETACHES: it clears the image and its asset id off this service and
       leaves the asset itself alone. */
    remove: () => {
      cancelImageUpload();
      set((f) => withServiceImage(f, "", null));
      setImageUpload(SERVICE_IMAGE_IDLE);
    },
    /* A pasted URL is an explicit override: it clears the uploaded asset id, so
       the record never pairs a URL with an asset it did not come from. */
    setUrl: (url) => {
      if (serviceImageBusy(imageUpload)) { cancelImageUpload(); setImageUpload(SERVICE_IMAGE_IDLE); }
      set((f) => withServiceImage(f, url, null));
    },
  };

  const SECTIONS: { id: string; label: string; icon: ReactNode; badge?: number }[] = [
    { id: "overview",  label: t("services.editorPage.overview", "Overview"),           icon: <Icon.info size={16} /> },
    { id: "delivery",  label: t("services.editorPage.deliveryMethods", "Delivery methods"),   icon: <Icon.layers size={16} />, badge: form.methods.length },
    { id: "booking",   label: t("services.editorPage.bookingApproval", "Booking & approval"), icon: <Icon.check size={16} /> },
    { id: "staff",     label: t("services.editorPage.staffResources", "Staff & resources"),  icon: <Icon.users size={16} /> },
    { id: "coverage",  label: t("services.editorPage.coverage", "Coverage"),           icon: <Icon.check size={16} /> },
    { id: "targeting", label: t("services.editorPage.targetingExtras", "Targeting & extras"), icon: <Icon.filter size={16} />,
      /* JAI-1767 placement ruling (2026-09-21): the count makes a set hold
         visible without opening the section — the same affordance Delivery
         methods already has. `undefined` at zero, so no "0" chip appears. */
      badge: form.resolvesHolds?.length || undefined },
  ];

  const save = async () => {
    if (serviceImageBusy(imageUpload)) { toast({ tone: "warn", title: t("services.editorPage.heroStillUploading", "The hero image is still uploading"), subtitle: t("services.editorPage.waitForTheUploadThenSave", "Wait for the upload to finish, then save.") }); setSection("overview"); return; }
    if (!form.name.trim()) { toast({ tone: "warn", title: t("services.editorPage.nameYourServiceFirst", "Name your service first") }); setSection("overview"); return; }
    if (!form.methods.length) { toast({ tone: "warn", title: t("services.editorPage.enableAtLeastOneDeliveryMethod", "Enable at least one delivery method") }); setSection("delivery"); return; }
    // Publish guard (D-1 … D-6): never publish a service with a layer that would
    // contribute zero slots. High-severity readiness blockers stop the publish.
    if (form.status === "published") {
      const blockers = serviceReadiness(form).filter((w) => w.level === "high");
      if (blockers.length) {
        toast({ tone: "warn", title: t("services.editorPage.canTPublishBlockersLengthCoverageBlockerBlockersLength1S.icu", "Can't publish — {count, plural, one {# coverage blocker} other {# coverage blockers}}", { count: blockers.length }), subtitle: blockers[0].msg });
        setSection("coverage");
        return;
      }
    }
    try {
      // Re-derived at save so only a durable URL and its confirmed asset id are
      // written. A detached image saves as null on both spellings; no asset the
      // save superseded is deleted — it may be referenced elsewhere.
      const keptAssetId = serviceImageAssetIdOf(form);
      await store.upsertService(withServiceImage(form, serviceImageOf(form), keptAssetId));
      toast({ tone: "success", title: isNew ? "Service created" : "Service saved", subtitle: form.status === "published" ? "Published and available to book." : "Saved as a draft." });
      nav.go("services");
    } catch (e) {
      const perCondition = audienceErrorsFrom(t, e);
      const storeDown = audienceStoreUnavailable(e);
      if (perCondition) {
        setAudienceErrors(perCondition); setAudienceNotice(null); setSection("targeting");
        toast({ tone: "danger", title: t("services.editorPage.couldnTSaveService", "Couldn't save service"), subtitle: t("audience.error.saveInvalid", "Check the highlighted audience conditions. Nothing was saved.") });
        return;
      }
      if (storeDown) {
        setAudienceErrors(null); setSection("targeting");
        const msg = storeDown.offeringSaved
          ? t("audience.error.storeUnavailableSaved", "Your other changes were saved, but the audience wasn't. Save again to retry.")
          : t("audience.error.storeUnavailable", "The audience couldn't be saved right now, so nothing was created. Save again to retry.");
        setAudienceNotice(msg);
        toast({ tone: "danger", title: t("services.editorPage.couldnTSaveService", "Couldn't save service"), subtitle: msg });
        return;
      }
      toast({ tone: "danger", title: t("services.editorPage.couldnTSaveService", "Couldn't save service"), subtitle: t("services.editorPage.nothingWasSavedYourEditsAreStillHereRetryInAMoment", "Nothing was saved — your edits are still here. Retry in a moment.") });
    }
  };

  if (svcPending || svcNotFound) {
    return (
      <Screen rail="cs-services">
        <div style={{ padding: "48px 24px", display: "flex", justifyContent: "center" }}>
          {svcPending
            ? <div role="status" aria-live="polite" style={{ fontSize: 13, color: T.soft }}>{t("services.editorPage.text2", "Loading the service…")}</div>
            : <EmptyState variant="card" icon={<Icon.info size={22} />} title={t("services.editorPage.notFoundTitle", "This service doesn't exist")}
                body={t("common.notFound.body", "It may have been deleted, or the link is out of date. Nothing was created.")}
                action={<Btn onClick={() => nav.go("services")}>{t("services.editorPage.backToServices", "Back to services")}</Btn>} />}
        </div>
      </Screen>
    );
  }

  return (
    <Screen rail="cs-services">
      <PageHeader
        title={isNew ? t("services.screens.newService", "New service") : (form.name || t("services.serviceEditorPage.editService", "Edit service"))}
        subtitle={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>{svcStatusPill(form.status)} · {svcCategoryName(form.category)} · {form.methods.length} {t("services.editorPage.deliveryMethod", "delivery method")}{" "}{form.methods.length === 1 ? "" : t("services.editorPage.text", "s")}</span>}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("services")}><Icon.arrow dir="left" size={11} /> {t("services.editorPage.services", "Services")}</span>}
        actions={viewOnly ? <Btn icon={<Icon.edit size={14} />} onClick={() => nav.go("services.edit", { id: form.id })}>{t("common.actions.edit", "Edit")}</Btn> : <>
          <Btn variant="secondary" onClick={() => nav.go("services")}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.check size={14} />} onClick={save}>{isNew ? t("services.serviceEditorPage.createService", "Create service") : t("common.actions.save", "Save")}</Btn>
        </>}
      />
      <div style={{ flex: 1, overflow: "hidden", display: "flex", minHeight: 0 }}>
        {/* left section nav */}
        <nav style={{ width: 236, flexShrink: 0, borderRight: `1px solid ${T.border}`, padding: "18px 14px", overflow: "auto", background: T.canvas }}>
          {SECTIONS.map((s) => {
            const on = section === s.id;
            return (
              /* ui-raw: left-hand editor section rail row — EditorSectionNav's pattern (group 8), not Button's */
              <button key={s.id} onClick={() => setSection(s.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", marginBottom: 4, borderRadius: 9, border: "none", background: on ? T.white : "transparent", color: on ? T.p1 : T.n2, fontWeight: on ? 700 : 500, fontSize: 13.5, cursor: "pointer", fontFamily: T.font, boxShadow: on ? T.shadowSm : "none", textAlign: "left" }}>
                <span style={{ color: on ? T.p1 : T.soft, display: "inline-flex" }}>{s.icon}</span>
                <span style={{ flex: 1 }}>{s.label}</span>
                {s.badge ? <span style={{ fontSize: 11, fontWeight: 700, color: on ? T.p1 : T.soft, background: on ? T.p4 : T.n6, borderRadius: 999, padding: "1px 7px" }}>{s.badge}</span> : null}
              </button>
            );
          })}
          <div style={{ marginTop: 16, padding: "12px", borderRadius: 10, background: T.white, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: T.soft, marginBottom: 8 }}>{t("services.editorPage.status", "Status")}</div>
            <SvcSeg value={form.status} onChange={(v) => set({ status: v })} disabled={!canWrite} options={[{ id: "draft", label: t("common.status.draft", "Draft") }, { id: "published", label: t("common.status.published", "Published") }]} />
          </div>
        </nav>

        {/* right content */}
        <div style={{ flex: 1, overflow: "auto", padding: "24px 28px 60px", minWidth: 0 }}>
          <div style={{ maxWidth: 760 }}>
            {section === "overview" && <OverviewSection form={form} set={set} canWrite={canWrite} image={image} />}
            {section === "delivery" && <ServiceDeliverySection form={form} set={set} setMethodCfg={setMethodCfg} canWrite={canWrite} />}
            {section === "booking" && <BookingSection form={form} set={set} canWrite={canWrite} />}
            {section === "staff" && <StaffSection form={form} set={set} canWrite={canWrite} />}
            {section === "coverage" && <CoverageSection form={form} canWrite={canWrite} goStaff={() => setSection("staff")} />}
            {section === "targeting" && <TargetingSection form={form} set={set} canWrite={canWrite}
              audienceErrors={audienceErrors} audienceNotice={audienceNotice}
              onAudienceEdited={() => { setAudienceErrors(null); setAudienceNotice(null); }} />}
          </div>
        </div>
      </div>
    </Screen>
  );
}

type SectionProps = { form: Service; set: (p: Partial<Service> | ((f: Service) => Partial<Service>)) => void; canWrite: boolean };

/* ── Overview ────────────────────────────────────────────────────────────── */
function OverviewSection({ form, set, canWrite, image }: SectionProps & { image: ServiceImageHandlers }) {
  const t = useT();
  const store = useServices();
  const cats = store.categories();
  const chan = (id: string) => form.channels.includes(id);
  const toggleChan = (id: string) => set({ channels: chan(id) ? form.channels.filter((c: string) => c !== id) : [...form.channels, id] });
  return (
    <div>
      <SvcSectionHead icon={<Icon.info size={18} />} title={t("services.editorPage.overview", "Overview")} desc={t("services.editorPage.desc", "What the service is and where it can be booked.")} />
      <SvcRow label={t("services.editorPage.serviceName", "Service name")} hint={t("services.editorPage.hint18", "{arg1}-facing title.", { arg1: tenantLabel("customer", t("common.customer", "Customer")) })}>
        <Input value={form.name} onChange={(e) => set((f) => ({ name: e.target.value, landing: { ...f.landing, slug: f.landing && f.landing.slug && !f._slugTouched ? _svcSlug(e.target.value) : (f.landing ? f.landing.slug : _svcSlug(e.target.value)) } }))} placeholder={t("services.editorPage.eG11FinancialConsultation", "e.g. 1:1 Financial Consultation")} disabled={!canWrite} />
      </SvcRow>
      <SvcRow label={t("services.editorPage.description", "Description")} hint={t("services.editorPage.hint2", "A short summary shown to customers.")}>
        <textarea value={form.description || ""} onChange={(e) => set({ description: e.target.value })} disabled={!canWrite} rows={3} placeholder={t("services.editorPage.whatThisServiceOffers", "What this service offers")} style={{ width: "100%", border: `1px solid ${T.n4}`, borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: T.font, color: T.n1, resize: "vertical", background: canWrite ? T.white : T.n6, boxSizing: "border-box" }} />
      </SvcRow>
      <SvcRow label={t("services.editorPage.heroImage", "Hero image")} hint={t("services.editorPage.heroImageHint", "Upload a PNG or JPEG, up to 5 MB.")} full>
        <ServiceImageField image={serviceImageOf(form)} assetId={serviceImageAssetIdOf(form)} handlers={image} disabled={!canWrite} />
      </SvcRow>
      <SvcRow label={t("services.editorPage.category", "Category")} hint={t("services.editorPage.hint4", "Optional — groups the service for packages. Defaults to None.")}>
        <div style={{ maxWidth: 320 }}><SvcSelect value={form.category || ""} onChange={(v) => set({ category: v })} disabled={!canWrite} options={[{ id: "", label: t("services.editorPage.none", "None") }, ...cats.map((c) => ({ id: c.id, label: c.name }))]} /></div>
      </SvcRow>
      <SvcRow label={t("services.editorPage.visibility", "Visibility")} hint={t("services.editorPage.hint5", "Public lists the service in the Appointments Hub. Private hides it — reachable only via its landing-page link. Visibility is global to the service.")}>
        <SvcSeg value={form.visibility} onChange={(v) => set({ visibility: v })} disabled={!canWrite} options={[{ id: "public", label: t("services.editorPage.public", "Public") }, { id: "private", label: t("services.editorPage.private", "Private") }]} />
      </SvcRow>
      <SvcRow label={t("services.editorPage.supportedChannels", "Supported channels")} hint={t("services.editorPage.hint6", "A service can be offered through any combination.")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {SVC_CHANNELS.map((c) => (
            <label key={c.id} onClick={canWrite ? () => toggleChan(c.id) : undefined} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 13px", borderRadius: 10, border: `1px solid ${chan(c.id) ? T.p2 : T.border}`, background: chan(c.id) ? T.p4 : T.white, cursor: canWrite ? "pointer" : "default" }}>
              <Checkbox checked={chan(c.id)} />
              <span style={{ width: 28, height: 28, borderRadius: 7, background: c.hue + "1f", color: c.hue, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><SvcChannelGlyph id={c.id} size={15} /></span>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{c.label}</span>
            </label>
          ))}
        </div>
      </SvcRow>
    </div>
  );
}

/* ── Booking & approval ──────────────────────────────────────────────────── */
function BookingSection({ form, set, canWrite }: SectionProps) {
  const t = useT();
  return (
    <div>
      <SvcSectionHead icon={<Icon.check size={18} />} title={t("services.editorPage.bookingApproval", "Booking & approval")} desc={t("services.editorPage.desc2", "What happens when a customer books.")} />
      <SvcRow label={t("services.editorPage.confirmation", "Confirmation")} hint={t("services.editorPage.hint7", "Booking questions, pricing and comms are set per delivery method, under Delivery.")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {([["auto", "Confirm on booking", "The booking is confirmed immediately."], ["review", "Require staff review", "A staff member must review and approve each booking before it's confirmed."]] as const).map(([id, t, d]) => (
            <label key={id} onClick={canWrite ? () => set({ confirmation: id }) : undefined} style={{ display: "flex", gap: 12, padding: "13px 15px", borderRadius: 11, border: `1px solid ${form.confirmation === id ? T.p2 : T.border}`, background: form.confirmation === id ? T.p4 : T.white, cursor: canWrite ? "pointer" : "default" }}>
              <span style={{ marginTop: 1 }}><Radio on={form.confirmation === id} /></span>
              <span><span style={{ fontSize: 14.5, fontWeight: 700, color: T.n1, display: "block" }}>{t}</span><span style={{ fontSize: 13, color: T.soft }}>{d}</span></span>
            </label>
          ))}
        </div>
      </SvcRow>
      {form.confirmation === "review" ? (
        <div style={{ marginTop: 14, display: "flex", gap: 11, padding: "13px 15px", borderRadius: 11, background: T.p4, border: `1px solid ${T.p2}` }}>
          <span style={{ color: T.p1, flexShrink: 0 }}><Icon.info size={16} /></span>
          <p style={{ margin: 0, fontSize: 13, color: T.n2 }}>{t("services.editorPage.bookingsForThisServiceWillLandInAReviewQueueUntilA", "Bookings for this service will land in a review queue. Until a staff member approves, the slot is held but not confirmed — the customer sees a \"pending approval\" state.")}</p>
        </div>
      ) : null}
      <SvcRow label={t("services.editorPage.availabilityExceptions", "Availability exceptions")} hint={t("services.editorPage.hint8", "Service-wide one-off dates: pause the whole service (e.g. a launch freeze) or open it on dates outside the weekly grids (S-4). Applies on top of staff & resource exceptions.")} full>
        <ExceptionList items={form.exceptions} onChange={(next) => set({ exceptions: next })} canWrite={canWrite} allowExtra />
      </SvcRow>
    </div>
  );
}

/* ── Staff & resources ───────────────────────────────────────────────────── */
function StaffSection({ form, set, canWrite }: SectionProps) {
  const t = useT();
  const groups = useStaff().groups;
  const rgroups = useResources().groups;
  const assigned: string[] = form.staffGroups || [];
  const staffing: { groupId: string; count: number }[] = form.staffing || [];
  const resourcing: { groupId: string; count: number }[] = form.resourcing || [];
  const toggleGroup = (gid: string) => set({ staffGroups: assigned.includes(gid) ? assigned.filter((x) => x !== gid) : [...assigned, gid] });
  const addReq = () => set({ staffing: [...staffing, { groupId: (groups[0] || { id: "" }).id, count: 1 }] });
  const setReq = (i: number, patch: Record<string, any>) => set({ staffing: staffing.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const rmReq = (i: number) => set({ staffing: staffing.filter((_, idx) => idx !== i) });
  const addRes = () => set({ resourcing: [...resourcing, { groupId: (rgroups[0] || { id: "" }).id, count: 1 }] });
  const setRes = (i: number, patch: Record<string, any>) => set({ resourcing: resourcing.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const rmRes = (i: number) => set({ resourcing: resourcing.filter((_, idx) => idx !== i) });

  return (
    <div>
      <SvcSectionHead icon={<Icon.users size={18} />} title={t("services.editorPage.staffResources", "Staff & resources")} desc={t("services.editorPage.desc3", "What it takes to deliver — staff and resources are each optional.")} />
      <SvcRow label={t("services.editorPage.requiresAPerson", "Requires a person")} hint={t("services.editorPage.hintRequiresPerson", "Whether someone from your team must deliver each booking.")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <Toggle
            on={form.requiresPerson !== false}
            label={form.requiresPerson !== false ? t("settings.security.on", "On") : t("account.details.off", "Off")}
            onChange={canWrite ? ((v: boolean) => set({ requiresPerson: v })) : undefined}
          />
          {/* The consequence, not the setting restated. "Off" is the one that
              surprises: it removes the service from the public booking page,
              and an author who discovers that after publishing has already
              shipped a service nobody can book. */}
          <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.5, maxWidth: 460 }}>
            {form.requiresPerson !== false
              ? t("services.editorPage.requiresPersonOn", "Bookable against your team's working hours.")
              : t("services.editorPage.requiresPersonOff", "Bookable only against hours set on the service itself; not offered on the public booking page.")}
          </div>
        </div>
      </SvcRow>
      <SvcRow label={t("services.editorPage.deliveredByGroups", "Delivered by groups")} hint={t("services.editorPage.hint9", "Staff groups whose members can deliver this service. Members inherit it automatically. Optional.")}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {groups.length === 0 ? <div style={{ fontSize: 13, color: T.soft }}>{t("services.editorPage.noStaffGroupsYetCreateThemUnderAdministrationStaffGroups", "No staff groups yet — create them under Administration → Staff → Groups.")}</div> : groups.map((g: any) => { const on = assigned.includes(g.id); return (
            <Chip key={g.id} selected={on} disabled={!canWrite} onClick={() => toggleGroup(g.id)}
              leading={<span style={{ width: 9, height: 9, borderRadius: 3, background: g.hue, display: "inline-block" }} />}>{g.name}</Chip>
          ); })}
        </div>
      </SvcRow>
      <SvcRow label={t("services.editorPage.staffingRequirement", "Staffing requirement")} hint={t("services.editorPage.hint10", "How many staff — and from which group — are needed to run ONE booking. Add a row per role (e.g. 1 Attorney + 1 Accountant). Leave empty if no staff is required.")} full>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
          {staffing.length === 0 ? <div style={{ fontSize: 13, color: T.soft, padding: "4px 0" }}>{t("services.editorPage.noStaffRequiredThisServiceCanRunWithoutADedicatedStaffMember", "No staff required — this service can run without a dedicated staff member.")}</div> : staffing.map((r, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", border: `1px solid ${T.border}`, borderRadius: 9, background: T.white }}>
              <SvcNumField value={r.count} onChange={(v) => setReq(i, { count: v })} min={1} max={20} unit="×" width={84} disabled={!canWrite} />
              <span style={{ fontSize: 13, color: T.soft }}>{t("services.editorPage.from", "from")}</span>
              <div style={{ flex: 1, minWidth: 0 }}><SvcSelect value={r.groupId} onChange={(v) => setReq(i, { groupId: v })} disabled={!canWrite} options={groups.map((g: any) => ({ id: g.id, label: g.name }))} /></div>
              {canWrite ? <IconBtn size={32} onClick={() => rmReq(i)}><Icon.x size={14} /></IconBtn> : null}
            </div>
          ))}
          {canWrite && groups.length ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={addReq}>{t("services.editorPage.addStaffRequirement", "Add staff requirement")}</Btn></div> : null}
        </div>
      </SvcRow>
      <SvcRow label={t("services.editorPage.resourceRequirement", "Resource requirement")} hint={t("services.editorPage.hint11", "Which resources — and how many — a booking must reserve (e.g. 1 Meeting room + 1 Projector). Their schedules are factored into availability. Leave empty if no resource is required.")} full>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
          {resourcing.length === 0 ? <div style={{ fontSize: 13, color: T.soft, padding: "4px 0" }}>{t("services.editorPage.noResourceRequiredThisServiceCanRunWithoutReservingAResource", "No resource required — this service can run without reserving a resource.")}</div> : resourcing.map((r, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", border: `1px solid ${T.border}`, borderRadius: 9, background: T.white }}>
              <SvcNumField value={r.count} onChange={(v) => setRes(i, { count: v })} min={1} max={20} unit="×" width={84} disabled={!canWrite} />
              <span style={{ fontSize: 13, color: T.soft }}>{t("services.editorPage.from", "from")}</span>
              <div style={{ flex: 1, minWidth: 0 }}><SvcSelect value={r.groupId} onChange={(v) => setRes(i, { groupId: v })} disabled={!canWrite} options={rgroups.map((g: any) => ({ id: g.id, label: g.name }))} /></div>
              {canWrite ? <IconBtn size={32} onClick={() => rmRes(i)}><Icon.x size={14} /></IconBtn> : null}
            </div>
          ))}
          {canWrite && rgroups.length ? <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={addRes}>{t("services.editorPage.addResourceRequirement", "Add resource requirement")}</Btn></div> : null}
          {canWrite && rgroups.length === 0 ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("services.editorPage.createResourceGroupsUnderAdministrationResourcesGroupsToRequireOneHere", "Create resource groups under Administration → Resources → Groups to require one here.")}</div> : null}
          {(staffing.length + resourcing.length) > 1 ? <div style={{ fontSize: 12, color: T.soft, display: "flex", gap: 7, alignItems: "center", marginTop: 2 }}><Icon.info size={13} /> {t("services.editorPage.aBookingIsOnlyOfferedWhen", "A booking is only offered when")}{" "}<b style={{ color: T.n2 }}>{t("services.editorPage.all", "all")}</b> {t("services.editorPage.staffAndResourceRequirementsCanBeMetAtTheSameTime", "staff and resource requirements can be met at the same time.")}</div> : null}
        </div>
      </SvcRow>
      <SvcRow label={t("services.editorPage.concurrency", "Concurrency")} hint={t("services.editorPage.hint12", "How many of this service can run at the same time, per staff member.")}>
        <SvcNumField value={form.concurrency} onChange={(v) => set({ concurrency: v })} min={1} max={50} unit="at once" width={140} disabled={!canWrite} />
      </SvcRow>
      {/* Guests per booking — read-only (JAI-2328): set on the Details step of the appointment journey;
          the service shows the largest limit among the live journeys that offer it. */}
      <SvcRow label={t("services.editorPage.guests", "Guests")} hint="Set on the Details step of the appointment journey. This shows the largest limit among the live journeys that offer this service.">
        <span style={{ fontSize: 13, color: T.n1 }}>
          {form.allowGuests === false ? "Off — the journeys that offer this service don't allow guests."
            : form.allowGuests == null ? "No live journey offers this service yet, so no guest limit applies."
            : `Up to ${form.maxGuests ?? 0} ${form.maxGuests === 1 ? "guest" : "guests"} per booking.`}
        </span>
      </SvcRow>
    </div>
  );
}

/* ── Targeting & extras ──────────────────────────────────────────────────── */
function TargetingSection({ form, set, canWrite, audienceErrors, audienceNotice, onAudienceEdited }: SectionProps & {
  audienceErrors: AudienceErrors | null; audienceNotice: string | null; onAudienceEdited: () => void;
}) {
  const t = useT();
  const store = useServices();
  const products = useProducts();
  const others = store.services().filter((s) => s.id !== form.id);
  const togglePre = (sid: string) => set({ prereqs: form.prereqs.includes(sid) ? form.prereqs.filter((x: string) => x !== sid) : [...form.prereqs, sid] });
  const attachedIds = products.productsForService(form.id).map((p) => p.id);
  const activeProducts = products.products.filter((p) => p.status === "active" || attachedIds.includes(p.id));
  const toggleProduct = (pid: string) => {
    const next = attachedIds.includes(pid) ? attachedIds.filter((x) => x !== pid) : [...attachedIds, pid];
    products.setServiceProducts(form.id, next);
  };
  return (
    <div>
      <SvcSectionHead icon={<Icon.filter size={18} />} title={t("services.editorPage.targetingExtras", "Targeting & extras")} desc={t("services.editorPage.desc4", "Who it's for and what's attached.")} />
      <SvcRow label={t("services.editorPage.locationScope", "Location scope")} hint={t("services.editorPage.hint13", "Which branches / stores offer this service, from Administration → Locations. Leave empty for all locations.")}>
        <SvcLocationPicker value={form.locScope} onChange={(v) => set({ locScope: v })} disabled={!canWrite} />
      </SvcRow>
      {/* JAI-1767 — the categories booking this service clears. Rides the
          existing updateStudioService body; no new op.

          FIRST IN THE SECTION, under Location scope (placement ruling,
          2026-09-21). It shipped last, below Prerequisite services — 60+ chips
          on a real tenant — and below Tags, so the product owner opened the
          section and reported "I see no holds on services". A control nobody
          scrolls to is a control that does not exist; keep it above every
          long list. */}
      <SvcRow label={holdsResolvesLabel(t)}>
        <ResolvesHoldsPicker value={form.resolvesHolds} subject="service" t={t} disabled={!canWrite}
          onChange={(v) => set({ resolvesHolds: v })} />
      </SvcRow>
      {/* JAI-2020 — Audience, its own row beside Resolves holds. Data: partial
          (lib/audience.tsx): attributes from listAudienceAttributes; the value is
          sent by ServicesStore.upsertService only when it changed. */}
      <SvcRow label={t("audience.section.title", "Audience")}>
        <AudienceSection value={form.audience} subject="service" t={t} disabled={!canWrite}
          errors={audienceErrors} sectionError={audienceNotice}
          onChange={(v) => { onAudienceEdited(); set({ audience: v } as unknown as Partial<Service>); }} />
      </SvcRow>
      <SvcRow label={t("services.editorPage.products", "Products")} hint={t("services.editorPage.hint14", "Products offered alongside this service (appointment & queue services included). Manage the catalog under Products. One product can be attached to many services.")}>
        {activeProducts.length === 0 ? <div style={{ fontSize: 13, color: T.soft }}>{t("services.editorPage.noProductsYetAddThemUnderTheProductsRailItem", "No products yet — add them under the Products rail item.")}</div> : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {activeProducts.map((p) => { const on = attachedIds.includes(p.id); return (
              /* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */
              <button key={p.id} type="button" disabled={!canWrite} onClick={() => toggleProduct(p.id)} style={{ padding: "6px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, border: `1px solid ${on ? T.p1 : T.n4}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n2, cursor: canWrite ? "pointer" : "default", fontFamily: T.font }}>{on ? "✓ " : ""}{p.name}{p.price > 0 ? t("services.editorPage.text9", "· {arg1}", { arg1: productPriceLabel(p) }) : ""}</button>
            ); })}
          </div>
        )}
      </SvcRow>
      <SvcRow label={t("services.editorPage.prerequisiteServices", "Prerequisite services")} hint={t("services.editorPage.hint15", "Services a customer must have booked before this one.")}>
        {others.length === 0 ? <div style={{ fontSize: 13, color: T.soft }}>{t("services.editorPage.noOtherServicesYet", "No other services yet.")}</div> : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {others.map((s) => { const on = form.prereqs.includes(s.id); return (
              /* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */
              <button key={s.id} type="button" disabled={!canWrite} onClick={() => togglePre(s.id)} style={{ padding: "6px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, border: `1px solid ${on ? T.p1 : T.n4}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n2, cursor: canWrite ? "pointer" : "default", fontFamily: T.font }}>{on ? "✓ " : ""}{s.name}</button>
            ); })}
          </div>
        )}
      </SvcRow>
      <SvcRow label={t("services.editorPage.tags", "Tags")} hint={t("services.editorPage.hint16", "Labels for search, filtering and reporting.")}>
        <SvcTagField tags={form.tags} onChange={(v) => set({ tags: v })} disabled={!canWrite} placeholder={t("services.editorPage.addATag", "Add a tag…")} />
      </SvcRow>
      <SvcRow label={t("services.editorPage.postServiceFeedback", "Post-service feedback")} hint={t("services.editorPage.hint17", "Trigger a review / satisfaction request after the service completes.")}>
        <Toggle on={form.feedback} label={form.feedback ? t("settings.security.on", "On") : t("account.details.off", "Off")} onChange={canWrite ? (v: boolean) => set({ feedback: v }) : undefined} />
      </SvcRow>
    </div>
  );
}

/* ── Coverage (D-1 … D-6) ─────────────────────────────────────────────────
 * Reads the shared readiness/coverage helpers and flags every layer that
 * would contribute zero slots, plus a location × channel matrix of qualified
 * staff / resources. The publish guard in save() blocks on the High rows.
 * (The intersection engine ports as its own module — until then the adapter
 * reports no findings and this section renders its empty states.) */
function CoverageSection({ form, goStaff, canWrite }: { form: Service; goStaff: () => void; canWrite: boolean }) {
  const t = useT();
  const warns = serviceReadiness(form);
  const matrix = serviceCoverageMatrix(form);
  const tone: Record<string, { bg: string; bd: string; fg: string; label: string }> = {
    high: { bg: T.red2, bd: T.red1, fg: T.red1, label: t("services.editorPage.blocker", "Blocker") },
    med:  { bg: T.orange2, bd: T.orange1, fg: "#8A5A12", label: t("services.editorPage.warning", "Warning") },
    low:  { bg: T.p4, bd: T.p1, fg: T.p1, label: t("services.editorPage.note", "Note") },
  };
  const high = warns.filter((w) => w.level === "high").length;
  const cell = (n: number | null) => {
    if (n == null) return <span style={{ color: T.soft, fontSize: 12 }}>—</span>;
    const ok = n > 0;
    return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 700, fontSize: 13, color: ok ? T.green1 : T.red1 }}>{ok ? <Icon.check size={12} /> : <Icon.warn size={12} />}{n}</span>;
  };
  return (
    <div>
      <SvcSectionHead icon={<Icon.check size={18} />} title={t("services.editorPage.coverage", "Coverage")} desc={t("services.editorPage.desc5", "Whether the intersection engine could actually fill this service's slots.")} />

      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 15px", borderRadius: 11, marginBottom: 16,
        background: warns.length === 0 ? T.green2 : high ? T.red2 : T.orange2,
        border: `1px solid ${warns.length === 0 ? T.green1 : high ? T.red1 : T.orange1}` }}>
        <span style={{ color: warns.length === 0 ? T.green1 : high ? T.red1 : "#8A5A12", flexShrink: 0 }}>{warns.length === 0 ? <Icon.check size={17} /> : <Icon.warn size={17} />}</span>
        <div style={{ fontSize: 13.5, color: T.n1 }}>
          {warns.length === 0
            ? <><b>{t("services.editorPage.readyToPublish", "Ready to publish.")}</b> {t("services.editorPage.everyRequiredLayerHasQualifiedCoverageAcrossTheInScopeLocationsAnd", "Every required layer has qualified coverage across the in-scope locations and channels.")}</>
            : <><b>{high ? t("services.editorPage.blockerCount", "{count, plural, one {# blocker} other {# blockers}}", { count: high }) : t("services.editorPage.text4", "No blockers")}{" "}{warns.length - high ? ", " + t("services.editorPage.warningCount", "{count, plural, one {# warning} other {# warnings}}", { count: warns.length - high }) : ""}.</b> {high ? t("services.editorPage.text6", "Fix these before you publish.") : t("services.editorPage.text7", "This service is ready to publish.")}</>}
        </div>
      </div>

      {warns.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {warns.map((w, i) => { const t = tone[w.level] || tone.low; return (
            <div key={i} style={{ display: "flex", gap: 11, padding: "11px 13px", borderRadius: 10, background: t.bg, borderLeft: `3px solid ${t.bd}` }}>
              <span style={{ fontFamily: T.fontMono, fontSize: 10.5, fontWeight: 700, color: t.fg, flexShrink: 0, paddingTop: 1 }}>{t.label}</span>
              <div style={{ fontSize: 13, color: T.n1, lineHeight: 1.5, flex: 1 }}>{w.msg}</div>
              <span style={{ fontFamily: T.fontMono, fontSize: 10, color: T.soft, flexShrink: 0 }}>{w.code}</span>
            </div>
          ); })}
          {canWrite ? <div style={{ marginTop: 2 }}><Btn variant="secondary" size="sm" icon={<Icon.users size={13} />} onClick={goStaff}>{t("services.editorPage.editStaffResourceRequirements", "Edit staff & resource requirements")}</Btn></div> : null}
        </div>
      ) : null}

      {matrix.rows.length ? (
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: T.n1, marginBottom: 8 }}>{t("services.editorPage.qualifiedCoverageLocationChannel", "Qualified coverage — location × channel")}</div>
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 11, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: T.font }}>
              <thead>
                <tr style={{ background: T.canvas, color: T.soft, fontSize: 10.5, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.04 }}>
                  <th style={{ textAlign: "left", padding: "9px 13px" }}>{t("services.editorPage.location", "Location")}</th>
                  <th style={{ textAlign: "left", padding: "9px 13px" }}>{t("services.editorPage.channel", "Channel")}</th>
                  {matrix.needsStaff ? <th style={{ textAlign: "left", padding: "9px 13px" }}>{t("services.editorPage.staff", "Staff")}</th> : null}
                  {matrix.needsRes ? <th style={{ textAlign: "left", padding: "9px 13px" }}>{t("services.editorPage.resources", "Resources")}</th> : null}
                  <th style={{ textAlign: "left", padding: "9px 13px" }}>{t("services.editorPage.venueHours", "Venue hours")}</th>
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((r, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${T.border}` }}>
                    <td style={{ padding: "9px 13px", color: T.n1, fontWeight: 600 }}>{r.locName}</td>
                    <td style={{ padding: "9px 13px", color: T.n2 }}>{r.channel}</td>
                    {matrix.needsStaff ? <td style={{ padding: "9px 13px" }}>{cell(r.staff)}</td> : null}
                    {matrix.needsRes ? <td style={{ padding: "9px 13px" }}>{cell(r.resources)}</td> : null}
                    <td style={{ padding: "9px 13px" }}>{r.hasHours ? <Pill tone="success" dot>{t("services.editorPage.set", "Set")}</Pill> : <Pill tone="warn" dot>{t("services.editorPage.notSet", "Not set")}</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!matrix.needsStaff && !matrix.needsRes ? <div style={{ fontSize: 12.5, color: T.soft, marginTop: 9 }}>{t("services.editorPage.thisServiceRequiresNeitherStaffNorAResourceSoOnlyTheVenue", "This service requires neither staff nor a resource, so only the venue hours and the method schedule bound its slots.")}</div> : null}
        </div>
      ) : (
        <div style={{ padding: "26px 16px", textAlign: "center", color: T.soft, border: `1.5px dashed ${T.border}`, borderRadius: 12, fontSize: 13 }}>{t("services.editorPage.noInScopeLocationsToEvaluateSetALocationScopeUnderTargeting", "No in-scope locations to evaluate — set a location scope under Targeting & extras.")}</div>
      )}
    </div>
  );
}
